import { Box, Typography } from '@mui/material';
import { GridToolbarContainer } from '@mui/x-data-grid';
import OmmnioDataGrid from 'components/common/OmmnioDataGrid';
import PeopleSelected from 'components/@home/drawers/TeamDrawer/Form/controls/EmployeesTable/Toolbar/PeopleSelected';
import Search from 'components/@home/drawers/TeamDrawer/Form/controls/EmployeesTable/Toolbar/Search';
import { useListQuery } from 'store/app/entities/EmployeesApiSlice';
import authCompanyId from 'store/selectors/authCompanyId';
import { arrayOf, func, number, shape, string } from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDebounceCallback } from 'utils/useDebounceCallback';
import { I18n } from 'utils/i18n';
import {
  mergePageSelectionIds,
  toIncludeSelectionModel,
} from 'components/@home/@documents/SharedDocuments/rowSelectionIds';

const RecipientsToolbar = ({ selectedCount, onSearch }) => (
  <GridToolbarContainer
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 1,
      pr: 1,
      pl: 1,
      py: 1,
      borderBottom: 1,
      borderColor: 'divider',
    }}
  >
    <Typography component="div" variant="subtitle1" sx={{ fontWeight: 600 }}>
      {I18n.t('TeamDrawer.EmployeesTable.Select member for this team')}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <PeopleSelected selected={selectedCount} />
      <Search onChange={onSearch} isFetching={false} />
    </Box>
  </GridToolbarContainer>
);

RecipientsToolbar.propTypes = {
  selectedCount: number.isRequired,
  onSearch: func.isRequired,
};

const OmmnioRecipientsTable = ({ selectedIds, onChangeSelectedIds, onChangeSelectedRows }) => {
  const rowSelectionModel = useMemo(() => toIncludeSelectionModel(selectedIds), [selectedIds]);
  const companyId = useSelector(authCompanyId);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortModel, setSortModel] = useState([{ field: 'name', sort: 'asc' }]);
  const [search, setSearch] = useState('');

  const order = useMemo(() => {
    const sm = sortModel?.[0];
    if (!sm?.field) return 'name';
    return `${sm.sort === 'desc' ? '-' : ''}${sm.field}`;
  }, [sortModel]);

  const {
    data: listPayload,
    isLoading,
    isFetching,
  } = useListQuery(
    {
      companyId,
      search,
      skip: page * pageSize,
      limit: pageSize,
      order,
      excludeApps: true,
    },
    { skip: !companyId },
  );

  const metadata = listPayload?.metadata;
  const total = metadata?.total ?? 0;

  const rows = useMemo(() => listPayload?.data || [], [listPayload?.data]);

  const columns = useMemo(
    () => [
      {
        field: 'name',
        flex: 1.4,
        minWidth: 180,
        headerName: I18n.t('TeamDrawer.EmployeesTable.Name'),
        sortable: true,
        valueGetter: (_value, row) =>
          [row.firstName, row.surName].filter(Boolean).join(' ').trim() || '—',
      },
      {
        field: 'code',
        flex: 0.7,
        minWidth: 90,
        headerName: I18n.t('HrBot.Code'),
        sortable: true,
        valueGetter: (_value, row) => row.code || '—',
      },
      {
        field: 'department',
        flex: 1,
        minWidth: 130,
        headerName: I18n.t('TeamDrawer.EmployeesTable.Department'),
        sortable: true,
        valueGetter: (_value, row) => row.department || '—',
      },
      {
        field: 'jobTitle',
        flex: 1,
        minWidth: 130,
        headerName: I18n.t('TeamDrawer.EmployeesTable.Job Position'),
        sortable: true,
        valueGetter: (_value, row) => row.jobTitle || '—',
      },
      {
        field: 'location',
        flex: 1,
        minWidth: 120,
        headerName: I18n.t('TeamDrawer.EmployeesTable.Location'),
        sortable: true,
        valueGetter: (_value, row) => row.location || '—',
      },
    ],
    [],
  );

  const handleSearchCommit = useCallback(value => {
    setSearch(value);
    setPage(0);
  }, []);

  const debouncedSearchCommit = useDebounceCallback(handleSearchCommit, 700);

  const handleToolbarSearch = useCallback(
    e => {
      const v = e.target.value;
      if (v === '') {
        debouncedSearchCommit.cancel();
        handleSearchCommit('');
        return;
      }
      debouncedSearchCommit(v);
    },
    [debouncedSearchCommit, handleSearchCommit],
  );

  const handleSortModelChange = useCallback(model => {
    setSortModel(model);
    setPage(0);
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        flex: 1,
        minHeight: { xs: 280, sm: 360 },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ flex: 1, minHeight: { xs: 280, sm: 360 }, width: '100%' }}>
        <OmmnioDataGrid
          loading={isLoading || isFetching}
          sx={{
            flex: 1,
            minHeight: 0,
            height: '100%',
            borderWidth: 0,
            '& .MuiDataGrid-main': {
              borderTop: '1px solid',
              borderColor: 'divider',
            },
          }}
          columns={columns}
          rows={rows}
          getRowId={r => String(r._id)}
          rowCount={total}
          paginationMode="server"
          sortingMode="server"
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          defaultSortModel={[{ field: 'name', sort: 'asc' }]}
          disableEvenOdd
          checkboxSelection
          keepNonExistentRowsSelected
          disableRowSelectionExcludeModel
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={model => {
            const pageIds = rows.map(row => String(row._id));
            const ids = mergePageSelectionIds(model, pageIds, selectedIds);
            onChangeSelectedIds(ids);
            if (onChangeSelectedRows) onChangeSelectedRows(ids, rows);
          }}
          slots={{ toolbar: RecipientsToolbar }}
          slotProps={{
            toolbar: {
              selectedCount: selectedIds.length,
              onSearch: handleToolbarSearch,
            },
            footer: { totalRowCount: total },
          }}
        />
      </Box>
      {!isLoading && rows.length === 0 && (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            py: 2,
          }}
        >
          {I18n.t('HrBot.RecipientsPicker.noEmployees')}
        </Typography>
      )}
    </Box>
  );
};

OmmnioRecipientsTable.propTypes = {
  selectedIds: arrayOf(string).isRequired,
  onChangeSelectedIds: func.isRequired,
  onChangeSelectedRows: func,
  selectedRecipientsForChips: arrayOf(shape({ id: string.isRequired, label: string.isRequired })),
  onRemoveRecipientChip: func,
};

export default OmmnioRecipientsTable;
